import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from '@emotion/styled';

import { Button, Link, NumberInput, Switch, Text, tokens, useNotify } from '@hotjar/hotsauce';

import { useIntegrations } from '../../hooks/use-integrations';
import { googleAnalyticsApi } from '../../domains/google-analytics/api';
import { IntegrationTokenName } from '../../domains/api';
import type { GoogleAnalyticsSettings } from '../../domains/api';

const DESCRIPTION_MAX_WIDTH = 280;

const SwitchTitle = styled(Text)`
  margin: ${tokens.spacer.x2} 0 ${tokens.spacer.x1};
`;

const FormContainer = styled.div`
  display: flex;
  margin-left: ${tokens.spacer.x6};
`;

const InputContainer = styled.div`
  width: 280px;
  margin-right: ${tokens.spacer.x1};
`;

const ActionDescription = styled(Text)`
  margin-left: ${tokens.spacer.x6};
  margin-bottom: ${tokens.spacer.x1};
  max-width: ${DESCRIPTION_MAX_WIDTH}px;
  margin-top: -${tokens.spacer.x1};
`;

const ExternalLink = styled(Link)`
  color: ${tokens.color.text_light};
`;

const isDimensionIndexValid = (value: string) => value.match(/^$|^[1-9][0-9]*$/);

interface GoogleAnalyticsActionsProps {
  siteId: string | null;
  actionLabel: Record<string, string>;
  isAdmin: boolean;
}

export const GoogleAnalyticsActions = ({
  siteId,
  actionLabel,
  isAdmin,
}: GoogleAnalyticsActionsProps): JSX.Element => {
  const { showToast } = useNotify();
  const { t } = useTranslation();

  const [useAutoEvents, toggleUseAutoEvents] = useState(false);
  const [useUserIds, toggleUseUserIds] = useState(false);
  const [inputDimensionIndex, setInputDimensionIndex] = useState<string>('');
  const { integrations, refreshTokens } = useIntegrations(Number(siteId));

  const { settings = null } =
    integrations.find(
      (integration) => integration.tokenName === IntegrationTokenName.GoogleAnalytics,
    ) ?? {};

  useEffect(() => {
    if (settings === null) {
      return;
    }

    toggleUseAutoEvents((settings as GoogleAnalyticsSettings).tag_sessions ?? false);
    toggleUseUserIds((settings as GoogleAnalyticsSettings).send_hotjar_id ?? false);
    setInputDimensionIndex((settings as GoogleAnalyticsSettings).hjuid_dimension_index ?? '');
  }, [settings]);

  const handleUseAutoEventsSwitchChange = async (isChecked: boolean) => {
    const { data } = await googleAnalyticsApi.update({
      sendHotjarUserId: useUserIds,
      dimensionIndex: inputDimensionIndex,
      tagSessions: isChecked,
      siteId: Number(siteId),
    });

    const state = isChecked ? 'enabled' : 'disabled';

    if (data.success) {
      toggleUseAutoEvents(isChecked);

      refreshTokens();
      showToast({
        text: t(`integrations:google_analytics.success.auto-events-${state}`),
        kind: 'success',
      });
    } else {
      showToast({
        text: t(`integrations:google_analytics.error.auto-events-${state}`),
        kind: 'danger',
      });
    }
  };

  const handleUseUserIdsSwitchChange = async (isChecked: boolean) => {
    const { data } = await googleAnalyticsApi.update({
      sendHotjarUserId: isChecked,
      dimensionIndex: inputDimensionIndex,
      tagSessions: useAutoEvents,
      siteId: Number(siteId),
    });

    const state = isChecked ? 'enabled' : 'disabled';

    if (data.success) {
      toggleUseUserIds(isChecked);

      refreshTokens();
      showToast({
        text: t(`integrations:google_analytics.success.hj-user-ids-${state}`),
        kind: 'success',
      });
    } else {
      showToast({
        text: t(`integrations:google_analytics.error.hj-user-ids-${state}`),
        kind: 'danger',
      });
    }
  };

  const handleGAFormSubmit = async (event: React.MouseEvent) => {
    event.preventDefault();

    const { data } = await googleAnalyticsApi.update({
      sendHotjarUserId: true,
      dimensionIndex: inputDimensionIndex,
      tagSessions: useAutoEvents,
      siteId: Number(siteId),
    });

    if (data.success) {
      setInputDimensionIndex(inputDimensionIndex);

      refreshTokens();
      showToast({
        text: t(`integrations:google_analytics.success.hj-user-ids-index-updated`),
        kind: 'success',
      });
    } else {
      showToast({
        text: t(`integrations:google_analytics.error.hj-user-ids-index-updated`),
        kind: 'danger',
      });
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (isDimensionIndexValid(value)) {
      setInputDimensionIndex(event.target.value);
    }
  };

  return (
    <>
      <SwitchTitle textType="subtitle2">{t(actionLabel.useEventsTitle)}</SwitchTitle>
      <Switch
        labelContent={t(actionLabel.useEventsCopy)}
        isChecked={useAutoEvents}
        onChange={handleUseAutoEventsSwitchChange}
        isDisabled={!isAdmin}
      />
      <SwitchTitle textType="subtitle2">{t(actionLabel.useUserIdsTitle)}</SwitchTitle>
      <Switch
        labelContent={t(actionLabel.useUserIdsCopy)}
        isChecked={useUserIds}
        onChange={handleUseUserIdsSwitchChange}
        isDisabled={!isAdmin}
      />
      <ActionDescription>
        <Trans i18nKey="integrations:details.header.google-analytics-use-user-ids-ua-note">
          For Universal Analytics, create a custom dimension and enter the index number from your
          <ExternalLink
            kind="inline"
            isExternal
            as="a"
            href="https://support.google.com/tagmanager/answer/6164990?hl=en"
          >
            Custom Dimension page
          </ExternalLink>
        </Trans>
      </ActionDescription>

      <FormContainer>
        <InputContainer>
          {/* Not using the DS Field component since the label text is really large and looks bad in relation
      to the integration title "Google Analytics". This would require redesign work. */}
          <NumberInput
            id="google-analytics-dimension-index"
            value={inputDimensionIndex}
            onChange={handleInputChange}
            isDisabled={!isAdmin || !useUserIds}
            placeholder={t('integrations:details.header.google-analytics-input-placeholder')}
          />
        </InputContainer>
        <Button kind="primary" isDisabled={!isAdmin || !useUserIds} onClick={handleGAFormSubmit}>
          {t('integrations:google_analytics.button-action')}
        </Button>
      </FormContainer>
    </>
  );
};
